/**
 * UnpostedDraftsWarning (PR 4b, "Draft notes in billing to warn CMs"): a
 * draft case note never bills until it is posted, and drafts live only
 * on each client's Notes tab, so an unposted one is invisible where
 * billing is checked. This names the signed-in person's own drafts with
 * a link to each client's Notes tab, where the draft opens.
 *
 * One read (GET /v1/my-note-drafts, the caller's own drafts, never the
 * text), two places:
 *   - My time: every draft, across clients.
 *   - A client's Pre-billing tab (`clientId` given): only the drafts for
 *     that client, filtered here from the same read.
 *
 * Renders nothing at zero, for a seat that cannot write a note (it can
 * hold no draft), and when the read fails: the warning is advisory, and
 * no raw error ever reaches the screen.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTenant } from '@/lib/auth';
import { canAuthorClinicalNotes } from '@/lib/capabilities';
import { proseDate } from '@/lib/proseDate';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/primitives/Button';
import { pageWriteText } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import { myDraftSummariesQueryKey, noteDraftsApi, useMyDraftSummaries, type MyDraftSummaryWire } from '@/lib/noteDrafts';

export interface UnpostedDraftsWarningProps {
  /** On a client's Pre-billing tab: that client. Absent on My time. */
  clientId?: string;
  /** Spacing from the host; applied only when the warning renders. */
  className?: string;
}

export function UnpostedDraftsWarning({ clientId, className = '' }: UnpostedDraftsWarningProps): ReactNode {
  const { whoami, tenantSlug } = useTenant();
  const enabled = canAuthorClinicalNotes(whoami);
  const data = useMyDraftSummaries(tenantSlug, { enabled });
  if (!enabled || data === null || !Array.isArray(data.drafts)) return null;

  const drafts = clientId === undefined ? data.drafts : data.drafts.filter((d) => d.client_id === clientId);
  // My time's number is the server's uncapped count; a client's is the
  // drafts listed for it (a person with over 50 drafts sees the newest).
  const count = clientId === undefined ? data.count : drafts.length;
  if (count <= 0) return null;

  const noun = count === 1 ? 'note draft' : 'note drafts';
  const headline =
    clientId === undefined
      ? `Your ${count} ${noun} ${count === 1 ? 'bills' : 'bill'} once you post ${count === 1 ? 'it' : 'them'}.`
      : `Your ${count} ${noun} for this client ${count === 1 ? 'bills' : 'bill'} once you post ${count === 1 ? 'it' : 'them'}.`;
  const more = count - drafts.length;

  return (
    <section
      data-testid="unposted-drafts-warning"
      aria-labelledby="unposted-drafts-heading"
      className={`space-y-2 rounded-tight border border-danger/40 bg-pepper-soft p-4 ${className}`}
    >
      <h2 id="unposted-drafts-heading" className="text-base font-semibold text-danger">
        {headline}
      </h2>
      <ul className="space-y-1">
        {drafts.map((d) =>
          d.out_of_scope === true ? (
            <OutOfScopeDraft key={d.id} draft={d} tenantSlug={tenantSlug} />
          ) : (
            <li key={d.id} data-testid={`unposted-draft-${d.id}`} className="text-sm text-ink">
              <Link
                to={`/t/${tenantSlug}/clients/${d.client_id}?tab=notes`}
                data-testid={`unposted-draft-link-${d.id}`}
                className="font-semibold text-danger underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
              >
                {clientId === undefined ? d.client_display_name : 'Open the Notes tab'}
              </Link>
              <span className="text-ink-soft">{`, visit on ${proseDate(d.occurred_on)}`}</span>
            </li>
          ),
        )}
      </ul>
      {more > 0 && <p className="text-sm text-ink">{`And ${more} more not listed here.`}</p>}
    </section>
  );
}

/**
 * Her own draft on a client she can no longer open: the client's Notes tab
 * would refuse her, so the row offers Discard only (the drafts DELETE
 * policy keeps an author arm for exactly this). A failed discard shows the
 * error copy and keeps the row.
 */
function OutOfScopeDraft({ draft, tenantSlug }: { draft: MyDraftSummaryWire; tenantSlug: string }): ReactNode {
  const queryClient = useQueryClient();
  const contact = useSupportContact();
  const discard = useMutation({
    mutationFn: (vars: { slug: string; draftId: string }) => noteDraftsApi.discard(vars.slug, vars.draftId),
    onSuccess: (_data, vars) => queryClient.invalidateQueries({ queryKey: myDraftSummariesQueryKey(vars.slug) }),
  });
  return (
    <li data-testid={`unposted-draft-${draft.id}`} className="text-sm text-ink">
      <span className="font-semibold">{draft.client_display_name}</span>
      <span className="text-ink-soft">{`, visit on ${proseDate(draft.occurred_on)}. `}</span>
      <span data-testid="draft-out-of-scope" className="text-ink-soft">
        No longer on your clients
      </span>
      <Button
        variant="secondary"
        size="sm"
        className="ml-2"
        data-testid={`draft-discard-${draft.id}`}
        disabled={discard.isPending}
        onClick={() => discard.mutate({ slug: tenantSlug, draftId: draft.id })}
      >
        Discard
      </Button>
      {discard.isError && (
        <p role="alert" className="mt-1 text-sm text-danger">
          {pageWriteText(discard.error, contact)}
        </p>
      )}
    </li>
  );
}
